/**
 * MODULE: _shared/frontend-shell/src/hooks/billing/useBillingSettings.ts
 * PURPOSE: Tenant token billing settings and credit top-up checkout
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getApiBaseUrl, getAuthHeaders } from '@shared/lib/frontend/api/config';

const API = `${getApiBaseUrl()}/api/v1/auth/subscription`;

export type BillingSettings = {
  monthly_spend_cap_usd: number | null;
  auto_topup_enabled: boolean;
  preferred_topup_pack_id: string;
  bonus_ai_tokens: number;
  period_overage_spend_usd: number;
};

export type CreditPack = {
  id: string;
  label: string;
  amount_usd: number;
  tokens: number;
  description: string;
};

async function fetchBillingSettings(): Promise<BillingSettings> {
  const res = await fetch(`${API}/billing-settings`, { headers: getAuthHeaders() });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `HTTP ${res.status}`);
  }
  return res.json();
}

async function fetchCreditPacks(): Promise<CreditPack[]> {
  const res = await fetch(`${API}/credit-packs`);
  if (!res.ok) throw new Error('Failed to load credit packs');
  const data = await res.json();
  return data.packs as CreditPack[];
}

export function useBillingSettings() {
  return useQuery({
    queryKey: ['billing', 'settings'],
    queryFn: fetchBillingSettings,
    staleTime: 30_000,
  });
}

export function useCreditPacks() {
  return useQuery({
    queryKey: ['billing', 'credit-packs'],
    queryFn: fetchCreditPacks,
    staleTime: 300_000,
  });
}

export function useUpdateBillingSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      monthly_spend_cap_usd?: number | null;
      auto_topup_enabled?: boolean;
      preferred_topup_pack_id?: string;
      unset_spend_cap?: boolean;
    }) => {
      const payload: Record<string, unknown> = {};
      if (body.unset_spend_cap) payload.unset_spend_cap = true;
      else if (body.monthly_spend_cap_usd != null) {
        payload.monthly_spend_cap_usd = body.monthly_spend_cap_usd;
      }
      if (body.auto_topup_enabled != null) payload.auto_topup_enabled = body.auto_topup_enabled;
      if (body.preferred_topup_pack_id != null) {
        payload.preferred_topup_pack_id = body.preferred_topup_pack_id;
      }
      const res = await fetch(`${API}/billing-settings`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to save settings');
      }
      return res.json() as Promise<BillingSettings>;
    },
    onSuccess: (data) => {
      qc.setQueryData(['billing', 'settings'], data);
      void qc.invalidateQueries({ queryKey: ['workspace-quota-ai'] });
    },
  });
}

export function useRedeemCreditVoucher() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (code: string) => {
      const res = await fetch(`${API}/redeem-credit-voucher`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ code: code.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.detail || 'Failed to redeem voucher');
      }
      return data as { success: boolean; bonus_ai_tokens?: number; tokens_granted?: number };
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['billing', 'settings'] });
      void qc.invalidateQueries({ queryKey: ['workspace-quota-ai'] });
    },
  });
}

export function useCreditTopupCheckout() {
  return useMutation({
    mutationFn: async (packId: string) => {
      const origin = typeof window !== 'undefined' ? window.location.origin : '';
      let email = '';
      try {
        email = (JSON.parse(localStorage.getItem('user') || '{}') as { email?: string }).email || '';
      } catch {
        /* ignore */
      }
      const tenantId = localStorage.getItem('tenant_id')?.replace(/^tnt_/, '') || undefined;
      const res = await fetch(`${API}/create-topup-checkout`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          pack_id: packId,
          email: email || 'billing@workspace.local',
          success_url: `${origin}/settings/ai-usage?topup=success`,
          cancel_url: `${origin}/settings/ai-usage?topup=canceled`,
          tenant_id: tenantId,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Checkout failed');
      if (data.url) window.location.href = data.url;
      else throw new Error('No checkout URL');
    },
  });
}
